import type { Grade } from '../types/domain';
import type { ScheduleTimeTag } from '../types/pointItemNote';
import { loadClassScheduleSettingsCache } from '../services/sectionLayoutService';
import { buildAnnualClassEntries, getSlotLimitForDay } from './annualTimetable';
import { dateToWeekdayKey } from './dashboardDateContext';
import { isWeekdayTimeKey, resolveClassScheduleRowsForWeekday } from './classScheduleWeekdayTimes';

function minutesFromHHMM(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec((hhmm || '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
  return h * 60 + min;
}

/** 수업시수 LS 캐시에서 오늘(또는 지정일) 교시 타임태그 목록 */
export function getScheduleTimeTagsForGrade(params: {
  workspaceId: string;
  gradeId: string;
  grades: Grade[];
  date?: Date;
}): ScheduleTimeTag[] {
  const { workspaceId, gradeId, grades, date = new Date() } = params;
  if (!workspaceId || !gradeId) return [];
  const schedule = loadClassScheduleSettingsCache(workspaceId);
  if (!schedule) return [];

  const entry = buildAnnualClassEntries(schedule, grades).find((e) => e.gradeId === gradeId);
  if (!entry) return [];

  const weekdayKey = dateToWeekdayKey(date);
  if (!weekdayKey || !isWeekdayTimeKey(weekdayKey)) return [];

  const todayMax = getSlotLimitForDay(entry.slotLimitByDay, weekdayKey);
  if (todayMax <= 0) return [];

  const resolvedRows = resolveClassScheduleRowsForWeekday(schedule, entry.sectionId, weekdayKey);

  const out: ScheduleTimeTag[] = [];
  let classOrdinal = 0;
  for (let i = 0; i < resolvedRows.length; i += 1) {
    const row = resolvedRows[i];
    if (row.kind !== 'class') continue;
    classOrdinal += 1;
    if (classOrdinal > todayMax) break;
    out.push({
      id: `sched-${gradeId}-${weekdayKey}-${i}`,
      name: (row.name || '').trim() || `${classOrdinal}교시`,
      startTime: row.startTime || '',
      endTime: row.endTime || '',
      dayKey: weekdayKey,
    });
  }
  return out;
}

/** 현재 시각에 해당하는 타임태그 (진행 중 → 없으면 곧 시작) */
export function resolveCurrentScheduleTimeTag(
  tags: ScheduleTimeTag[],
  now = new Date()
): ScheduleTimeTag | null {
  if (!tags.length) return null;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  for (const t of tags) {
    const start = minutesFromHHMM(t.startTime);
    const end = minutesFromHHMM(t.endTime);
    if (start == null || end == null) continue;
    if (nowMin >= start && nowMin < end) return t;
  }
  let best: ScheduleTimeTag | null = null;
  let bestDelta = Infinity;
  for (const t of tags) {
    const start = minutesFromHHMM(t.startTime);
    if (start == null || start < nowMin) continue;
    const d = start - nowMin;
    if (d < bestDelta) {
      bestDelta = d;
      best = t;
    }
  }
  return best;
}

export function formatScheduleTimeTagLabel(tag: ScheduleTimeTag | null): string {
  if (!tag) return '선택 안됨';
  const dayMap: Record<string, string> = {
    sun: '일',
    mon: '월',
    tue: '화',
    wed: '수',
    thu: '목',
    fri: '금',
    sat: '토',
  };
  const day = dayMap[tag.dayKey] ?? '';
  const range =
    tag.startTime && tag.endTime ? `${tag.startTime} ~ ${tag.endTime}` : '';
  // 커스텀 30분 태그(이름에 이미 '시' 포함)는 구간을 라벨에 붙이지 않음
  const isHalfHourName = /^\d{1,2}시(?:\s*\d{1,2}분)?$/.test((tag.name || '').trim());
  const body =
    isHalfHourName || !range ? tag.name : `${tag.name}${range ? ` ${range}` : ''}`;
  return day ? `[${day}] ${body}` : body;
}

/** 저장된 타임태그 라벨에서 이름 / 시간 구간 분리 */
export function splitScheduleTimeTagLabel(label: string): {
  name: string;
  timeRange?: string;
} {
  const trimmed = label.trim();
  if (!trimmed) return { name: '' };
  const m = /^(.*?)\s+(\d{1,2}:\d{2}\s*~\s*\d{1,2}:\d{2})\s*$/.exec(trimmed);
  if (!m) {
    const customTip = tipForHalfHourCustomTimeTagLabel(trimmed);
    return customTip ? { name: trimmed, timeRange: customTip } : { name: trimmed };
  }
  return {
    name: m[1].trim(),
    timeRange: m[2].replace(/\s*~\s*/, ' ~ '),
  };
}

const DAY_KO = ['일', '월', '화', '수', '목', '금', '토'] as const;

function formatMinutesAsHHMM(totalMin: number): string {
  const normalized = ((totalMin % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(normalized / 60);
  const m = normalized % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** 30분 단위로 내림한 커스텀 타임태그. 예: 토 10:32 → `[토] 10시 30분`, 토 11:01 → `[토] 11시` */
export function buildHalfHourCustomTimeTagLabel(at: Date): string {
  const day = DAY_KO[at.getDay()] ?? '';
  const slotted = Math.floor((at.getHours() * 60 + at.getMinutes()) / 30) * 30;
  const h = Math.floor(slotted / 60);
  const m = slotted % 60;
  const name = m === 0 ? `${h}시` : `${h}시 ${m}분`;
  return day ? `[${day}] ${name}` : name;
}

/**
 * 기록일(recordedDate)의 요일 + 생성시각(createdAt)의 시·분으로 커스텀 타임태그 생성.
 * createdAt이 없으면 fallbackNow(기본: 현재) 시각을 사용.
 */
export function buildHalfHourCustomTimeTagLabelForRecord(params: {
  recordedDate?: string;
  createdAt?: string | Date;
  fallbackNow?: Date;
}): string {
  const fallback = params.fallbackNow ?? new Date();
  const createdRaw =
    params.createdAt == null
      ? fallback
      : typeof params.createdAt === 'string'
        ? new Date(params.createdAt)
        : params.createdAt;
  const clock = Number.isNaN(createdRaw.getTime()) ? fallback : createdRaw;

  let at = clock;
  const iso = (params.recordedDate || '').trim();
  const dm = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (dm) {
    at = new Date(
      Number(dm[1]),
      Number(dm[2]) - 1,
      Number(dm[3]),
      clock.getHours(),
      clock.getMinutes(),
      clock.getSeconds(),
      clock.getMilliseconds()
    );
  }
  return buildHalfHourCustomTimeTagLabel(at);
}

/** 저장된 타임태그가 없으면 기록일·생성시각으로 커스텀 라벨을 돌려줌 */
export function resolveDisplayTimeTagLabel(params: {
  timeTagLabel?: string;
  recordedDate?: string;
  createdAt?: string;
}): string | undefined {
  const stored = (params.timeTagLabel || '').trim();
  if (stored) return stored;
  if (!params.recordedDate && !params.createdAt) return undefined;
  return buildHalfHourCustomTimeTagLabelForRecord(params);
}

/** `[토] 10시 30분` 형태 커스텀 라벨의 30분 구간 툴팁 */
export function tipForHalfHourCustomTimeTagLabel(label: string): string | undefined {
  const m = /(\d{1,2})시(?:\s*(\d{1,2})분)?\s*$/.exec(label.trim());
  if (!m) return undefined;
  const h = Number(m[1]);
  const min = m[2] != null ? Number(m[2]) : 0;
  if (!Number.isFinite(h) || h < 0 || h > 23 || (min !== 0 && min !== 30)) return undefined;
  const start = h * 60 + min;
  return `${formatMinutesAsHHMM(start)} ~ ${formatMinutesAsHHMM(start + 30)}`;
}

function normalizeHHMM(raw: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(raw.trim());
  if (!m) return raw.trim();
  return `${String(Number(m[1])).padStart(2, '0')}:${m[2]}`;
}

const KO_DAY_TO_KEY: Record<string, string> = {
  일: 'sun',
  월: 'mon',
  화: 'tue',
  수: 'wed',
  목: 'thu',
  금: 'fri',
  토: 'sat',
};

/**
 * 저장된/표시용 타임태그 라벨 → ScheduleTimeTag.
 * 커스텀(`[토] 10시 30분`)·수업시수(`[월] 1교시 09:00 ~ 09:40`) 모두 파싱.
 */
export function parseTimeTagLabelToScheduleTag(
  label: string | undefined | null,
  fallbackDayKey = 'mon'
): ScheduleTimeTag | null {
  const trimmed = (label || '').replace(/\s+/g, ' ').trim();
  if (!trimmed || trimmed === '선택 안됨') return null;

  let rest = trimmed;
  let dayKey = fallbackDayKey;
  const dayM = /^\[([일월화수목금토])\]\s*(.*)$/.exec(trimmed);
  if (dayM) {
    dayKey = KO_DAY_TO_KEY[dayM[1]] ?? fallbackDayKey;
    rest = (dayM[2] || '').trim();
  }
  if (!rest) return null;

  const rangeM = /^(.*?)\s+(\d{1,2}:\d{2})\s*~\s*(\d{1,2}:\d{2})\s*$/.exec(rest);
  if (rangeM) {
    const name = rangeM[1].trim();
    const startTime = normalizeHHMM(rangeM[2]);
    const endTime = normalizeHHMM(rangeM[3]);
    return {
      id: `custom-parsed-${dayKey}-${startTime}-${endTime}`,
      name: name || rest,
      startTime,
      endTime,
      dayKey,
    };
  }

  // `[일] 8시` / `8시` / `8시 30분` (전체 라벨에서도 시·분 추출)
  const halfM =
    /^(\d{1,2})시(?:\s*(\d{1,2})분)?\s*$/.exec(rest) ||
    /(\d{1,2})시(?:\s*(\d{1,2})분)?\s*$/.exec(trimmed);
  if (!halfM) return null;
  const h = Number(halfM[1]);
  const min = halfM[2] != null ? Number(halfM[2]) : 0;
  if (!Number.isFinite(h) || h < 0 || h > 23 || (min !== 0 && min !== 30)) return null;
  const start = h * 60 + min;
  const name = min === 0 ? `${h}시` : `${h}시 ${min}분`;
  return {
    id: `custom-half-${dayKey}-${start}`,
    name,
    startTime: formatMinutesAsHHMM(start),
    endTime: formatMinutesAsHHMM(start + 30),
    dayKey,
  };
}

/** Date → sun|mon|… (일요일 포함) */
export function dayKeyFromDate(d: Date): string {
  return (['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const)[d.getDay()] ?? 'mon';
}

/** 현재(또는 지정) 시각을 30분 단위 커스텀 ScheduleTimeTag로 변환 */
export function buildHalfHourCustomScheduleTag(
  at: Date = new Date(),
  dayKey?: string
): ScheduleTimeTag {
  const key = dayKey || dayKeyFromDate(at);
  const slotted = Math.floor((at.getHours() * 60 + at.getMinutes()) / 30) * 30;
  const h = Math.floor(slotted / 60);
  const m = slotted % 60;
  const name = m === 0 ? `${h}시` : `${h}시 ${m}분`;
  return {
    id: `custom-half-${key}-${slotted}`,
    name,
    startTime: formatMinutesAsHHMM(slotted),
    endTime: formatMinutesAsHHMM(slotted + 30),
    dayKey: key,
  };
}
